import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    TextField,
    Box,
    IconButton,
    Divider,
    Tabs,
    Tab
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add as AddIcon, Close as CloseIcon, AcUnit, Brightness5, Whatshot, TrendingFlat } from "@material-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getFunnelOpportunityNotes, createFunnelOpportunityNote } from "../../services/funnelOpportunityNotes";
import SchedulingTab from "../SchedulingTab";

const useStyles = makeStyles((theme) => ({
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },
    noteItem: {
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        borderRadius: 8,
        padding: theme.spacing(1)
    },
    noteDate: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5)
    },
    noteCard: {
        fontSize: "0.75rem",
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5)
    },
    noteText: {
        fontSize: "0.9rem",
        marginBottom: theme.spacing(0.5)
    },
    noteUser: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        fontStyle: "italic"
    },
    addNoteBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: 8
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: theme.palette.text.secondary
    },
    temperatureBox: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
        borderRadius: 12,
        fontSize: "0.75rem",
        fontWeight: 600,
        marginBottom: theme.spacing(0.5)
    },
    temperatureCold: {
        backgroundColor: "#e3f2fd",
        color: "#1976d2"
    },
    temperatureWarm: {
        backgroundColor: "#fff3e0",
        color: "#f57c00"
    },
    temperatureHot: {
        backgroundColor: "#ffebee",
        color: "#d32f2f"
    },
    movementBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5)
    },
    dialogPaper: {
        borderRadius: 12,
        boxShadow: "0px 25px 50px -12px rgba(0, 0, 0, 0.25) !important",
        border: "1px solid rgba(0, 0, 0, 0.05)"
    }
}));

const OpportunityHistoryModal = ({ open, onClose, opportunity, ticket }) => {
    const classes = useStyles();
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [showAddNote, setShowAddNote] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        if (open && (opportunity || ticket)) {
            fetchNotes();
        }
    }, [open, opportunity, ticket]);

    const fetchNotes = async () => {
        try {
            if (opportunity) {
                const { data } = await getFunnelOpportunityNotes(opportunity.id);
                setNotes(data);
            } else if (ticket && ticket.contact && ticket.contact.id) {
                // Sem prospecção, busca históricos por contactId
                const { data } = await getFunnelOpportunityNotes(null, ticket.contact.id);
                setNotes(data);
            } else {
                setNotes([]);
            }
        } catch (err) {
            toastError(err);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) {
            toast.error("Digite um histórico");
            return;
        }

        setLoading(true);
        try {
            if (opportunity) {
                await createFunnelOpportunityNote(opportunity.id, newNote.trim());
            } else {
                await createFunnelOpportunityNote(null, newNote.trim(), ticket.contact.id);
            }
            toast.success("Histórico adicionado com sucesso");
            setNewNote("");
            setShowAddNote(false);
            fetchNotes();
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        try {
            return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
        } catch {
            return "";
        }
    };

    const truncateText = (text, maxLength = 40) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    const getTemperatureDisplay = (temperature) => {
        if (!temperature) return null;

        const tempConfig = {
            cold: { icon: <AcUnit fontSize="small" />, label: "Frio", className: classes.temperatureCold },
            warm: { icon: <Brightness5 fontSize="small" />, label: "Morno", className: classes.temperatureWarm },
            hot: { icon: <Whatshot fontSize="small" />, label: "Quente", className: classes.temperatureHot }
        };

        const config = tempConfig[temperature];
        if (!config) return null;

        return (
            <Box className={`${classes.temperatureBox} ${config.className}`}>
                {config.icon}
                <span>{config.label}</span>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogTitle>
                <Box className={classes.header}>
                    <Typography variant="h6">
                        Históricos - {opportunity?.title || ticket?.contact?.name || "Contato"}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="fullWidth">
                <Tab label="Históricos" />
                <Tab label="Agendamentos" />
            </Tabs>

            <DialogContent dividers>
                {tabValue === 0 && (
                    <>
                        {notes.length === 0 ? (
                            <Typography className={classes.emptyState}>
                                Nenhum histórico registrado
                            </Typography>
                        ) : (
                            <List>
                                {notes.map((note, index) => (
                                    <React.Fragment key={note.id}>
                                        <ListItem className={classes.noteItem}>
                                            <ListItemText
                                                primary={
                                                    <>
                                                        <Typography className={classes.noteDate}>
                                                            {formatDate(note.createdAt)}
                                                        </Typography>
                                                        {note.type === "movement" && (
                                                            <Box className={classes.movementBadge}>
                                                                <TrendingFlat fontSize="small" />
                                                                <span>Movimentação</span>
                                                            </Box>
                                                        )}
                                                        {note.temperature && getTemperatureDisplay(note.temperature)}
                                                        <Typography className={classes.noteCard}>
                                                            {note.card?.name}
                                                        </Typography>
                                                        <Typography className={classes.noteText}>
                                                            {note.note}
                                                        </Typography>
                                                    </>
                                                }
                                                secondary={
                                                    <Typography className={classes.noteUser}>
                                                        Por: {note.user?.name || "Sistema"}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                        {index < notes.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}

                        {showAddNote ? (
                            <Box className={classes.addNoteBox}>
                                <TextField
                                    label="Novo Histórico"
                                    multiline
                                    rows={3}
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    margin="dense"
                                />
                                <Box mt={1} display="flex" gap={1} justifyContent="flex-end">
                                    <Button
                                        onClick={() => {
                                            setShowAddNote(false);
                                            setNewNote("");
                                        }}
                                        color="secondary"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleAddNote}
                                        color="primary"
                                        variant="contained"
                                        disabled={loading || !newNote.trim()}
                                    >
                                        Adicionar
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box mt={2} display="flex" justifyContent="center">
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowAddNote(true)}
                                    color="primary"
                                    variant="outlined"
                                >
                                    Adicionar Histórico
                                </Button>
                            </Box>
                        )}
                    </>
                )}

                {tabValue === 1 && (
                    <SchedulingTab opportunity={opportunity} ticket={ticket} />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OpportunityHistoryModal;
